import { useMemo, memo } from 'react';
import {
  Table,
  TypographyWithTooltip,
  useTranslations,
  IUseTableReturn,
  IUseQueryTableFetchReturn,
  IUseTableLocalizationReturn,
  IMaterialTableOptions,
} from '@uniqkey-frontend/shared-app';
import { prepareAuditLogRenderData } from '../../../helpers/auditLogs';
import {
  IOrganizationAuditLog,
  TGetOrganizationAuditLogsMethod,
} from '../../../hooks/usePartnerOrganizationsAPI/interfaces';

export interface IOrganizationAuditLogsTableRow extends IOrganizationAuditLog {
  processedDate: string | null;
  processedActionDoneBy: string | null;
  processedDescription: string;
}

interface IOrganizationAuditLogsTableProps extends
  Omit<
    IUseTableReturn<IOrganizationAuditLogsTableRow>,
    'columnOrderBy' | 'columnOrderDirection' | 'selectedRows' | 'resetTableMethods'
  >,
  Omit<
    IUseQueryTableFetchReturn<TGetOrganizationAuditLogsMethod>,
    'data' | 'resetQuery' | 'isFetchedAfterMount'
  >
{
  organizationAuditLogs: IUseQueryTableFetchReturn<TGetOrganizationAuditLogsMethod>['data'];
  selectedOrganizationAuditLogs: IUseTableReturn<IOrganizationAuditLogsTableRow>['selectedRows'];
  localization: IUseTableLocalizationReturn['localization'];
}

const TABLE_OPTIONS: IMaterialTableOptions<IOrganizationAuditLogsTableRow> = {
  selection: false,
};

const OrganizationAuditLogsTable = (props: IOrganizationAuditLogsTableProps) => {
  const {
    organizationAuditLogs: preOrganizationAuditLogs,
    isLoading,
    total,
    localization,
    activePage,
    setActivePage,
    perPage,
    onPerPageChange,
    onColumnOrderByChange,
    onColumnOrderDirectionChange,
    selectedOrganizationAuditLogs,
    onRowSelectionChange,
    createColumns,
  } = props;

  const { t } = useTranslations();

  const organizationAuditLogs = useMemo(
    () => preOrganizationAuditLogs.map(
      (organizationAuditLog) => prepareAuditLogRenderData(t, organizationAuditLog),
    ),
    [t, preOrganizationAuditLogs],
  );

  const columns = useMemo(() => createColumns([
    {
      title: t('organizationPage.auditLogsTab.date'),
      field: 'createdAt',
      render: (rowData) => (
        <TypographyWithTooltip variant="body1">
          {rowData.processedDate}
        </TypographyWithTooltip>
      ),
      width: '18%',
      sorting: false,
    },
    {
      title: t('organizationPage.auditLogsTab.actionDoneBy'),
      field: 'actorEmail',
      render: (rowData) => (
        <TypographyWithTooltip variant="body2">
          {rowData.processedActionDoneBy}
        </TypographyWithTooltip>
      ),
      width: '25%',
      sorting: false,
    },
    {
      title: t('organizationPage.auditLogsTab.description'),
      field: 'description',
      render: (rowData) => (
        <TypographyWithTooltip variant="body2">
          {rowData.processedDescription}
        </TypographyWithTooltip>
      ),
      width: '57%',
      sorting: false,
    },
  ]), [createColumns, t]);

  return (
    <Table
      t={t}
      totalItems={total}
      columns={columns}
      data={organizationAuditLogs}
      isLoading={isLoading}
      options={TABLE_OPTIONS}
      activePage={activePage}
      onActivePageChange={setActivePage}
      perPage={perPage}
      onPerPageChange={onPerPageChange}
      onColumnOrderByChange={onColumnOrderByChange}
      onColumnOrderDirectionChange={onColumnOrderDirectionChange}
      selectedRows={selectedOrganizationAuditLogs}
      onSelectionChange={onRowSelectionChange}
      localization={localization}
      rowSelectByKey="createdAt"
      rowHeight={51}
    />
  );
};

export default memo(OrganizationAuditLogsTable);
